<script>
import { toast } from "@zerodevx/svelte-toast";
import { fade, fly, slide } from "svelte/transition";
import { flip } from "svelte/animate";
import { cubicOut } from "svelte/easing";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import * as m from "@app/paraglide/messages.js";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import MemberPanel from "./MemberPanel.svelte";
import TeamCsvImport from "./TeamCsvImport.svelte";
import {
    TEAM_ROLES,
    DEFAULT_ROLE,
    normalizeRole,
    roleLabel,
    displayName,
    initials,
    isValidEmail,
    motion,
    errorMessage,
    saveMember,
    removeMember,
    addMemberToGroup,
    removeMemberFromGroup,
} from "./teamApi.js";

/** @type {{ teamId: string, ownerEmail: string, selfEmail: string, users: any[], groups: any[], seatLimit: number|null, onchange: () => Promise<void> }} */
let { teamId, ownerEmail, selfEmail, users, groups, seatLimit, onchange, onaddusers = null } = $props();

let search = $state(""),
    roleFilter = $state("all"),
    draft = $state(null),
    saving = $state(false),
    importing = $state(false);

const seatsFull = $derived(seatLimit != null && users.length >= seatLimit);

const filtered = $derived.by(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((user) => {
        if (roleFilter !== "all" && normalizeRole(user.role) !== roleFilter) return false;
        if (!needle) return true;
        return displayName(user).toLowerCase().includes(needle) || (user.email || "").toLowerCase().includes(needle);
    });
});

const groupNames = $derived(Object.fromEntries(groups.map((g) => [g.id, g.name || m.team_manager_unnamed_group()])));

function isOwner(user) {
    return !!user?.email && user.email === ownerEmail;
}

function openMember(user) {
    draft = {
        ...user,
        role: normalizeRole(user.role) || DEFAULT_ROLE,
        groups: [...(user.groups || [])],
        disable: !!user.disable,
        isNew: false,
    };
}

function openNewMember() {
    if (seatsFull) {
        toast.push(onaddusers ? m.team_manager_seats_full_owner({ limit: seatLimit }) : m.team_manager_seats_full_member({ limit: seatLimit, owner: ownerEmail }), { classes: ["toast-danger"] });
        return;
    }
    draft = { id: uuidv4(), firstName: "", lastName: "", email: "", role: DEFAULT_ROLE, disable: false, groups: [], isNew: true };
}

function validate(member) {
    const email = String(member.email || "").trim().toLowerCase();
    if (!email) throw new Error(m.team_manager_email_required());
    if (!isValidEmail(email)) throw new Error(m.team_manager_email_invalid());
    if (users.some((u) => u.id !== member.id && (u.email || "").toLowerCase() === email)) {
        throw new Error(m.team_manager_email_exists());
    }
    if (isOwner(member) && member.role !== "admin") throw new Error(m.team_manager_owner_must_be_admin({ email }));
    if (isOwner(member) && member.disable) throw new Error(m.team_manager_owner_cannot_disable({ email }));
}

async function save() {
    try {
        validate(draft);
    } catch (err) {
        toast.push(err.message, { classes: ["toast-danger"] });
        return;
    }

    saving = true;
    try {
        const before = draft.isNew ? [] : users.find((u) => u.id === draft.id)?.groups || [];
        await saveMember(teamId, draft);

        // Keep each group's member list in step with the user's groups.
        const added = draft.groups.filter((id) => !before.includes(id));
        const removed = before.filter((id) => !draft.groups.includes(id));
        for (const groupId of added) await addMemberToGroup(teamId, draft.id, groupId);
        for (const groupId of removed) await removeMemberFromGroup(teamId, draft.id, groupId);

        toast.push(draft.isNew ? m.team_manager_member_added_success({ email: draft.email.trim().toLowerCase() }) : m.team_manager_user_saved_success(), {
            classes: ["toast-success"],
        });
        draft = null;
        await onchange();
    } catch (err) {
        console.error(err);
        toast.push(m.team_manager_save_user_failed({ error: errorMessage(err) }), { classes: ["toast-danger"] });
    } finally {
        saving = false;
    }
}

async function remove() {
    const member = draft;
    const confirm = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.destructive,
        icon: "warning",
        title: m.team_manager_remove_member_confirm_title({ name: displayName(member) }),
        text: m.team_manager_remove_member_confirm_text(),
        showCancelButton: true,
        confirmButtonText: m.team_manager_remove_member_button(),
        cancelButtonText: m.cancel(),
        focusCancel: true,
    });
    if (!confirm.isConfirmed) return;

    saving = true;
    try {
        await removeMember(teamId, member);
        toast.push(m.team_manager_user_removed_success(), { classes: ["toast-success"] });
        draft = null;
        await onchange();
    } catch (err) {
        console.error(err);
        toast.push(m.team_manager_remove_user_failed({ error: errorMessage(err) }), { classes: ["toast-danger"] });
    } finally {
        saving = false;
    }
}

function rowKeydown(e, user) {
    if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openMember(user);
    }
}

async function importDone() {
    await onchange();
}
</script>

{#if importing}
    <div in:fly={{ y: 12, duration: motion(220), easing: cubicOut }}>
        <TeamCsvImport {teamId} {users} {seatLimit} onclose={() => (importing = false)} ondone={importDone} />
    </div>
{:else}
    <div in:fade={{ duration: motion(180) }}>
    <div class="team-toolbar">
        <div class="input-group input-group-sm team-search">
            <span class="input-group-text"><i class="bi bi-search" aria-hidden="true"></i></span>
            <input
                type="search"
                class="form-control"
                placeholder={m.team_manager_search_members_placeholder()}
                aria-label={m.team_manager_search_members_placeholder()}
                bind:value={search} />
        </div>
        <select class="form-select form-select-sm team-filter" aria-label={m.team_manager_filter_role_aria()} bind:value={roleFilter}>
            <option value="all">{m.team_manager_filter_all_roles()}</option>
            {#each TEAM_ROLES as role (role)}
                <option value={role}>{roleLabel(role)}</option>
            {/each}
        </select>
        <div class="ms-auto d-flex gap-2">
            <button type="button" class="btn btn-outline-secondary btn-sm" onclick={() => ((importing = true), (draft = null))} disabled={seatsFull}>
                <i class="bi bi-filetype-csv me-1"></i>{m.team_manager_import_csv_button()}
            </button>
            <button type="button" class="btn btn-primary btn-sm" onclick={openNewMember} disabled={seatsFull}>
                <i class="bi bi-person-plus me-1"></i>{m.team_manager_add_member_button()}
            </button>
        </div>
    </div>
    {#if seatsFull}
        <div class="alert alert-warning py-2 small mb-3" role="status" transition:slide={{ duration: motion(180) }}>
            <div class="d-flex flex-wrap align-items-center gap-2">
                <span><i class="bi bi-exclamation-triangle me-1"></i>{onaddusers ? m.team_manager_seats_full_owner({ limit: seatLimit }) : m.team_manager_seats_full_member({ limit: seatLimit, owner: ownerEmail })}</span>
                {#if onaddusers}
                    <button type="button" class="btn btn-warning btn-sm ms-auto" onclick={onaddusers}><i class="bi bi-person-plus me-1"></i>{m.team_manager_add_users_button()}</button>
                {/if}
            </div>
        </div>
    {/if}

    <div class="team-layout">
        <div class="team-table-wrap">
            {#if users.length === 0}
                <div class="team-empty">
                    <i class="bi bi-people" aria-hidden="true"></i>
                    <p class="fw-semibold mb-1">{m.team_manager_no_members_title()}</p>
                    <p class="small text-body-secondary mb-3">{m.team_manager_no_members_text()}</p>
                    <button type="button" class="btn btn-primary btn-sm" onclick={openNewMember}>{m.team_manager_add_member_button()}</button>
                </div>
            {:else if filtered.length === 0}
                <div class="team-empty">
                    <i class="bi bi-search" aria-hidden="true"></i>
                    <p class="small text-body-secondary mb-0">{m.team_manager_no_matches()}</p>
                </div>
            {:else}
                <table class="table table-hover align-middle mb-0 team-table">
                    <thead>
                        <tr>
                            <th scope="col">{m.team_manager_table_member()}</th>
                            <th scope="col">{m.team_manager_user_role_label()}</th>
                            <th scope="col">{m.team_manager_table_status()}</th>
                            <th scope="col" class="d-none d-lg-table-cell">{m.team_manager_groups_label()}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each filtered as user (user.id)}
                            <tr
                                animate:flip={{ duration: motion(200) }}
                                in:fade={{ duration: motion(160) }}
                                class={draft?.id === user.id ? "table-active" : ""}
                                tabindex="0"
                                aria-label={displayName(user)}
                                onclick={() => openMember(user)}
                                onkeydown={(e) => rowKeydown(e, user)}>
                                <td>
                                    <div class="d-flex align-items-center gap-2">
                                        <span class="team-avatar" aria-hidden="true">{initials(user)}</span>
                                        <div class="min-w-0">
                                            <div class="fw-semibold text-truncate">
                                                {displayName(user)}
                                                {#if isOwner(user)}<span class="badge soft-badge ms-1">{m.team_manager_owner_badge()}</span>{/if}
                                                {#if user.email === selfEmail}<span class="badge soft-badge ms-1">{m.team_manager_you_badge()}</span>{/if}
                                            </div>
                                            {#if user.firstName || user.lastName}
                                                <div class="small text-body-secondary text-truncate">{user.email}</div>
                                            {/if}
                                        </div>
                                    </div>
                                </td>
                                <td><span class="role-badge role-{normalizeRole(user.role) || DEFAULT_ROLE}">{roleLabel(user.role || DEFAULT_ROLE)}</span></td>
                                <td>
                                    {#if user.disable}
                                        <span class="status-dot status-off"></span>{m.team_manager_disabled()}
                                    {:else}
                                        <span class="status-dot status-on"></span>{m.team_manager_status_active()}
                                    {/if}
                                </td>
                                <td class="d-none d-lg-table-cell small text-body-secondary">
                                    {(user.groups || []).map((id) => groupNames[id]).filter(Boolean).join(", ") || "—"}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            {/if}
        </div>

        {#if draft}
            <div class="panel-slot" transition:fly={{ x: 40, duration: motion(240), easing: cubicOut }}>
                {#key draft.id}
                    <div in:fade={{ duration: motion(160) }}>
                        <MemberPanel
                            bind:draft
                            {groups}
                            isOwner={isOwner(draft)}
                            isSelf={draft.email === selfEmail}
                            {saving}
                            onsave={save}
                            oncancel={() => (draft = null)}
                            onremove={remove} />
                    </div>
                {/key}
            </div>
        {/if}
    </div>
    </div>
{/if}

<style>
    .min-w-0 {
        min-width: 0;
    }

    .team-search {
        max-width: 18rem;
    }

    .team-filter {
        width: auto;
    }

    .role-badge {
        display: inline-block;
        padding: 0.2rem 0.55rem;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 600;
        border: 1px solid transparent;
    }

    .role-admin {
        color: var(--bs-danger-text-emphasis);
        background: var(--bs-danger-bg-subtle);
        border-color: var(--bs-danger-border-subtle);
    }

    .role-manager {
        color: var(--bs-warning-text-emphasis);
        background: var(--bs-warning-bg-subtle);
        border-color: var(--bs-warning-border-subtle);
    }

    .role-editor {
        color: var(--bs-primary-text-emphasis);
        background: var(--bs-primary-bg-subtle);
        border-color: var(--bs-primary-border-subtle);
    }

    .role-viewer {
        color: var(--bs-secondary-text-emphasis);
        background: var(--bs-secondary-bg-subtle);
        border-color: var(--bs-secondary-border-subtle);
    }

    .status-dot {
        display: inline-block;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        margin-right: 0.4rem;
        vertical-align: middle;
    }

    .status-on {
        background: var(--bs-success);
    }

    .status-off {
        background: var(--bs-secondary-color);
    }
</style>
